import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, Link2 } from "lucide-react";
import { DEBT_CATEGORIES } from "@/lib/constants";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import HelpTip from "@/components/HelpTip";

export default function DebtModal({ open, onClose, onSaved, debt, defaultCategory }) {
  const { user } = useAuth();
  const empty = { name: "", balance: "", minimum_payment: "", interest_rate: "", category: defaultCategory || "credit_card", due_date: "", credit_limit: "" };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [members, setMembers] = useState([]);
  useEffect(() => { if (open) api.get("/household").then(r => setMembers(r.data.members || [])).catch(() => setMembers([])); }, [open]);

  useEffect(() => { setForm(debt ? { ...debt } : empty); /* eslint-disable-next-line */ }, [debt, open, defaultCategory]);
  if (!open) return null;

  const isCard = form.category === "credit_card";
  const zeroCard = isCard && parseFloat(form.balance || 0) === 0;

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        balance: parseFloat(form.balance) || 0,
        minimum_payment: parseFloat(form.minimum_payment) || 0,
        interest_rate: parseFloat(form.interest_rate) || 0,
        due_date: form.due_date,
        credit_limit: form.category === "credit_card" && form.credit_limit !== "" && form.credit_limit != null ? parseFloat(form.credit_limit) : null,
        statement_day: form.category === "credit_card" && form.statement_day !== "" && form.statement_day != null ? parseInt(form.statement_day, 10) : null,
        owner_id: form.owner_id || debt?.owner_id || user?.user_id || null,
        promo_rate: form.category === "credit_card" && form.promo_rate !== "" && form.promo_rate != null && form.promo_end ? parseFloat(form.promo_rate) : null,
        promo_end: form.category === "credit_card" && form.promo_rate !== "" && form.promo_rate != null && form.promo_end ? form.promo_end : null,
      };
      const label = isCard ? "Card" : "Loan";
      if (debt?.id) { await api.put(`/debts/${debt.id}`, payload); toast.success(`${label} updated`); }
      else { await api.post(`/debts`, payload); toast.success(`${label} added`); }
      onSaved?.(); onClose?.();
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="debt-modal-overlay">
      <form onClick={e => e.stopPropagation()} onSubmit={save} data-testid="debt-modal"
        className="w-full max-w-lg bg-card border border-border rounded-2xl p-6 shadow-2xl fade-up my-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display text-xl font-bold">{debt ? `Edit ${isCard ? "Credit Card" : "Loan"}` : isCard ? "Add Credit Card" : "Add Loan"}</h3>
          <button type="button" onClick={onClose} className="p-1 rounded-lg hover:bg-accent"><X className="h-4 w-4" /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <F label="Name" full><input required data-testid="debt-name-input" value={form.name} onChange={e => set("name", e.target.value)} className={inp} placeholder="Chase Sapphire" /></F>
          <F label={isCard ? "Current Balance ($0 is fine)" : "Balance"}><input required type="number" min="0" step="0.01" data-testid="debt-balance-input" value={form.balance} onChange={e => set("balance", e.target.value)} className={inp} placeholder={isCard ? "0" : ""} /></F>
          <F label={<>Min Payment <HelpTip k="min_payment" /></>}><input required type="number" min="0" step="0.01" data-testid="debt-min-input" value={form.minimum_payment} onChange={e => set("minimum_payment", e.target.value)} className={inp} placeholder={zeroCard ? "0" : ""} /></F>
          <F label={<>APR (%) <HelpTip k="apr" /></>}><input required type="number" step="0.01" data-testid="debt-apr-input" value={form.interest_rate} onChange={e => set("interest_rate", e.target.value)} className={inp} /></F>
          {!isCard && (
            <F label="Loan Type">
              <select value={form.category} onChange={e => set("category", e.target.value)} className={inp} data-testid="debt-category-select">
                {DEBT_CATEGORIES.filter(c => c.key !== "credit_card").map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </F>
          )}
          <F label="Next Due Date" full={isCard}><input required type="date" data-testid="debt-due-input" value={form.due_date || ""} onChange={e => set("due_date", e.target.value)} className={inp} /></F>
          {form.category === "credit_card" && (
            <>
            <F label={<>Promo APR % (optional) <HelpTip k="promo_apr" /></>}>
              <input type="number" min="0" step="0.01" data-testid="debt-promo-rate-input" value={form.promo_rate ?? ""} onChange={e => set("promo_rate", e.target.value)} className={inp} placeholder="0" />
            </F>
            <F label="Promo Ends On">
              <input type="date" data-testid="debt-promo-end-input" value={form.promo_end || ""} onChange={e => set("promo_end", e.target.value)} className={inp} />
              <div className="text-[11px] text-muted-foreground mt-1">Interest, Avalanche order and the simulator use the promo rate until this date, then the regular APR.</div>
            </F>
            <F label={<>Statement Closes On (day of month) <HelpTip k="statement_day" /></>}>
              <input type="number" min="1" max="31" data-testid="debt-statement-input" value={form.statement_day ?? ""} onChange={e => set("statement_day", e.target.value)} className={inp} placeholder="e.g. 15" />
              <div className="text-[11px] text-muted-foreground mt-1">Balance on this day is what gets reported to the bureaus — pay before it.</div>
            </F>
            {members.length > 1 && (
              <F label="Card Holder" full>
                <select value={form.owner_id || user?.user_id || ""} onChange={e => set("owner_id", e.target.value)} className={inp} data-testid="debt-owner-select">
                  {members.map(m => <option key={m.user_id} value={m.user_id}>{m.name}{m.user_id === user?.user_id ? " (you)" : ""}</option>)}
                </select>
                <div className="text-[11px] text-muted-foreground mt-1">Utilization is tracked per person as well as for the whole household.</div>
              </F>
            )}
            <F label={<>Credit Limit <HelpTip k="credit_limit" /></>}>
              <input type="number" step="0.01" min="0" data-testid="debt-limit-input" value={form.credit_limit ?? ""} onChange={e => set("credit_limit", e.target.value)} className={inp} placeholder="10000" />
              {parseFloat(form.credit_limit) > 0 && parseFloat(form.balance) >= 0 && (
                <div className="text-[11px] text-muted-foreground mt-1" data-testid="debt-limit-preview">
                  Utilization: <span className="font-mono font-semibold text-foreground">{Math.round((parseFloat(form.balance || 0) / parseFloat(form.credit_limit)) * 1000) / 10}%</span> · keep under 30% for a healthy score
                </div>
              )}
            </F>
            </>
          )}
        </div>
        <div className="mt-3 flex items-start gap-2 px-3 py-2.5 rounded-lg border border-indigo-500/30 bg-indigo-500/10 text-[11px] text-indigo-300" data-testid="debt-linked-bill-note">
          <Link2 className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>{zeroCard
            ? "A $0 card adds its limit to your total available credit and lowers overall utilization. No bill is due until it carries a balance."
            : `A monthly bill for the minimum payment is ${debt ? "kept in sync with" : "created automatically from"} this account. Marking it paid accrues one month of APR interest and reduces the balance.`}</span>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-border hover:bg-accent text-sm transition">Cancel</button>
          <button disabled={saving} type="submit" data-testid="debt-save-btn"
            className="px-5 py-2 rounded-lg bg-indigo-500 text-white font-semibold hover:bg-indigo-600 text-sm transition disabled:opacity-60">
            {saving ? "Saving…" : isCard ? "Save Card" : "Save Loan"}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
}

const inp = "w-full px-3 py-2 rounded-lg bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-500 transition";
function F({ label, children, full }) {
  return (
    <label className={`block ${full ? "col-span-2" : ""}`}>
      <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
